/**
 * Priority Select Component
 *
 * Dropdown selector for priorities with color preview.
 */

import { useEffect } from 'react'
import { cn } from '@/lib/utils'
import { usePriorities, useDefaultPriority } from '@/application/priorities/usePriorities'
import { Loader2 } from 'lucide-react'

interface PrioritySelectProps {
  /** Currently selected priority ID */
  value: string | null | undefined
  /** Callback when selection changes */
  onChange: (priorityId: string) => void
  /** Disabled state */
  disabled?: boolean
  /** Show color preview badge next to selector */
  showPreview?: boolean
  /** Additional CSS classes */
  className?: string
  /** Auto-select default priority on mount */
  autoSelectDefault?: boolean
  /** Placeholder text */
  placeholder?: string
}

/**
 * Priority selector with color preview
 */
export function PrioritySelect({
  value,
  onChange,
  disabled,
  showPreview = true,
  className,
  autoSelectDefault = true,
  placeholder = 'Seleccionar prioridad...',
}: PrioritySelectProps) {
  const { data: priorities, isLoading } = usePriorities()
  const { data: defaultPriority } = useDefaultPriority()

  // Auto-select default priority on mount
  useEffect(() => {
    if (autoSelectDefault && !value && defaultPriority) {
      onChange(defaultPriority.id)
    }
  }, [autoSelectDefault, value, defaultPriority, onChange])

  // Find selected priority for preview
  const selectedPriority = priorities?.find(p => p.id === value)

  if (isLoading) {
    return (
      <div className={cn('flex items-center gap-2', className)}>
        <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
        <span className="text-sm text-slate-400">Cargando prioridades...</span>
      </div>
    )
  }

  return (
    <div className={cn('flex items-center gap-3', className)}>
      {/* Select dropdown */}
      <select
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled || isLoading}
        className={cn(
          'flex-1 px-3 py-2 text-sm rounded-lg border transition-colors',
          'bg-white dark:bg-slate-800',
          'border-slate-300 dark:border-slate-600',
          'text-slate-900 dark:text-slate-100',
          'focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none',
          'disabled:opacity-50 disabled:cursor-not-allowed'
        )}
      >
        <option value="">{placeholder}</option>
        {priorities?.map((priority) => (
          <option key={priority.id} value={priority.id}>
            {priority.name}
            {priority.isDefault ? ' (Por defecto)' : ''}
          </option>
        ))}
      </select>

      {/* Color preview badge */}
      {showPreview && selectedPriority && (
        <span
          className="px-3 py-1.5 text-xs font-medium rounded-full whitespace-nowrap"
          style={{
            backgroundColor: selectedPriority.color,
            color: selectedPriority.textColor,
          }}
        >
          {selectedPriority.name}
        </span>
      )}
    </div>
  )
}

/**
 * Compact priority selector (just the dropdown, no preview)
 */
export function PrioritySelectCompact({
  value,
  onChange,
  disabled,
  className,
}: Omit<PrioritySelectProps, 'showPreview' | 'autoSelectDefault' | 'placeholder'>) {
  const { data: priorities, isLoading } = usePriorities()

  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled || isLoading}
      className={cn(
        'px-2 py-1 text-xs rounded border transition-colors',
        'bg-white dark:bg-slate-800',
        'border-slate-300 dark:border-slate-600',
        'text-slate-900 dark:text-slate-100',
        'focus:border-blue-500 focus:outline-none',
        'disabled:opacity-50 disabled:cursor-not-allowed',
        className
      )}
    >
      <option value="">-</option>
      {priorities?.map((priority) => (
        <option key={priority.id} value={priority.id}>
          {priority.name}
        </option>
      ))}
    </select>
  )
}

export default PrioritySelect
